import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FastField } from 'formik';
import * as Yup from 'yup';
import queryString from 'query-string';

import { successToast } from '../../store/slices/toaster.slice';
import { BaseButtonFill, BaseInput } from '..';
import { resetPassword } from '../../misc/requests';
import { passwordValidator } from '../../helpers/form.helper';

import UserCommonView from '../UserCommonView/UserCommonView';
import errorService, { ErrorService } from '../../helpers/errors.helper';

const RESET_ERRORS = {
  [ErrorService.ERRORS.UNDECODABLE_VALUE]: {
    type: 'warning',
    title: 'common:resetPassword.errors.errorTitle',
    content: 'common:resetPassword.errors.wrongUserId',
  },
  [ErrorService.ERRORS.INVALID_PASSWORD_RESET_TOKEN]: {
    type: 'warning',
    title: 'common:resetPassword.errors.errorTitle',
    content: 'common:resetPassword.errors.wrongToken',
  },
  [ErrorService.ERRORS.DOES_NOT_EXIST]: {
    type: 'warning',
    title: 'common:resetPassword.errors.errorTitle',
    content: 'common:resetPassword.errors.userNotFound',
  },
};

const UserResetPassword = ({ isAdvisor }) => {
  const { t: common } = useTranslation('common');

  const location = useLocation();
  const navigate = useNavigate();

  const [resetPasswordLoading, setResetPasswordLogin] = useState(false);

  const initialFormValues = {
    new_password: '',
    new_password_confirmation: '',
  };

  const query = queryString.parse(location.search);

  const validationSchema = Yup.object().shape(
    {
      new_password: passwordValidator(),
      new_password_confirmation: passwordValidator().oneOf(
        [Yup.ref('new_password'), null],
        common('errors.passwordsDontMatch')
      ),
    },
    [['new_password', 'new_password_confirmation']]
  );

  const onSubmit = async (formData) => {
    if (!query || !query.user_id || !query.token) {
      return;
    }

    try {
      setResetPasswordLogin(true);

      await resetPassword({ ...formData, ...query });

      successToast(common('resetPassword.success.successDescription'), common('resetPassword.success.successTitle'));
      navigate('/login');
    } catch (error) {
      errorService.handleErrorToast(error, RESET_ERRORS);
    } finally {
      setResetPasswordLogin(false);
    }
  };

  return (
    <UserCommonView
      isAdvisor={isAdvisor}
      heading={common('resetPassword.heading')}
      subheading={common('resetPassword.subheading')}
      initialFormValues={initialFormValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <div className="row">
        <FastField
          component={BaseInput}
          name="new_password"
          type="password"
          label={common('resetPassword.newPassword')}
          inputHint={common('passwordHint')}
          data-testid="password"
        />
      </div>
      <div className="row">
        <FastField
          component={BaseInput}
          name="new_password_confirmation"
          type="password"
          label={common('resetPassword.confirmNewPassword')}
          data-testid="confirmPassword"
        />
      </div>
      <div className="row">
        <BaseButtonFill
          type="submit"
          full
          iconRight
          icon="arrow-right"
          loading={resetPasswordLoading}
          data-testid="reset-password-button"
        >
          {common('resetPassword.button')}
        </BaseButtonFill>
      </div>
    </UserCommonView>
  );
};

export default UserResetPassword;
